import BlockIcon from "@mui/icons-material/Block";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LinkIcon from "@mui/icons-material/Link";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Popover, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Device } from "@twilio/voice-sdk";
import { isAxiosError } from "axios";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useModals } from "src/context/ModalContext";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { gatewayPhone } from "src/gateways/gatewayPhone";
import { registerVoiceSessionCleanup } from "src/gateways/voiceSessionCleanup";
import { formatUSPhone, menuPropsModal } from "src/utils";
import { useAppSelector } from "../../../redux/hooks/redux";
import { useRequireMicrophoneForCall } from "../../internal-contacts/_hooks/useRequireMicrophoneForCall";
import { getCallStartMediaErrorMessage } from "../../internal-contacts/_utils/requestMicrophoneAccess.utils";
import { blockedNumbersQueryKey, blockPhoneNumber, findBlockedNumber, unblockPhoneNumber } from "../blocked-numbers/blockNumber.action";
import { useBlockedNumbersQuery } from "../blocked-numbers/useBlockedNumbersQuery";
import { readInternalIncomingCall } from "../calls/helpers/readInternalIncomingCall";
import { splitPhoneFromE164 } from "../calls/helpers/splitNumbersFrom";
import { CompanyNumbersByCompany, CompanyNumberStatus, useCompanyNumbersMultiQuery } from "../porting-request/hooks/useCompanyNumbersMultiQuery";
import { SmsContact } from "../sms/contacts/types/SmsContact";
import { useSmsSocket } from "../sms/hooks/useSmsSocket";
import { IContact } from "../types/contacts.types";

/* ===================== TYPES ===================== */

export type CallDirection = "INCOMING" | "OUTGOING";
export type CallView = "dialer" | "incoming" | "in-call";

export type NormalizedPhone = {
  dialCode: string;
  phone: string;
};

export type CompanyRuntimeState = {
  companyId: string;
  sessionNumber: string;
  status: "initializing" | "ready" | "offline" | "error";
  errorMessage?: string;
  activeCall: any;
  callDirection: CallDirection | null;
  remotePhone: NormalizedPhone | null;
  isMuted: boolean;
  isOnHold: boolean;
  callDuration: number;
  isInternalIncoming?: boolean;
  internalCallerName?: string | null;
  internalCallerCompanyName?: string | null;
};

export type IncomingCompanyCall = {
  key: string;
  companyId: string;
  remotePhone: NormalizedPhone | null;
  activeCall: any;
  sessionNumber: string;
  isInternalIncoming?: boolean;
  internalCallerName?: string | null;
  internalCallerCompanyName?: string | null;
};

interface VrContextProps {
  companyNumbersData: CompanyNumbersByCompany[];

  status: "initializing" | "ready" | "offline" | "error";
  errorMessage?: string;
  sessionNumber: string;

  isOnHold: boolean;
  toggleHold: () => void;
  toggleHoldByCompany: (key: string) => void;

  activeCall: any;
  callDirection: CallDirection | null;
  remotePhone: NormalizedPhone | null;
  view: CallView;
  isMuted: boolean;
  callDuration: number;

  currentCompanyState: CompanyRuntimeState | null;
  activeCallCompanyName: string | null;
  companiesState: Map<string, CompanyRuntimeState>;
  incomingCalls: IncomingCompanyCall[];
  getCompanyCallState: (key: string) => CompanyRuntimeState | null;
  getCompanyView: (key: string) => CallView;

  currentPhoneNumber: string | null;
  getCurrentPhoneNumber: (companyId?: string) => string | null;

  makeCall: (to: string) => Promise<void>;

  acceptCall: () => void;
  hangupCall: () => void;
  toggleMute: () => void;
  sendDisconnect: (companyId: string) => Promise<void>;
  sendDigit: (digit: string) => void;

  acceptCallByCompany: (key: string) => void;
  hangupCallByCompany: (key: string) => void;
  toggleMuteByCompany: (key: string) => void;
  sendDigitByCompany: (key: string, digit: string) => void;
  handleTransfer: (key: string, targetAgentUid: string) => Promise<void>;

  selectedNumberUid: string;
  setSelectedNumberUid: (uid: string) => void;

  handleSelectContact: (contact: SmsContact | null) => void;
  selectedContact: SmsContact | null;
  mockIncomingCall: (companyId?: string) => void;
  contactToEdit: IContact | null;
  quickSavePhone: string | null;
  contactMode:
    | "create"
    | "link"
    | "both"
    | "link-client"
    | "link-workforce"
    | "link-prospect"
    | "link-applicant"
    | "link-vendor"
    | "link-vendor-prospect";
  linkClientPhone: string | null;
  linkProspectPhone: string | null;
  linkWorkforcePhone: string | null;
  linkApplicantPhone: string | null;
  linkVendorPhone: string | null;
  linkVendorProspectPhone: string | null;
  clearLinkClientPhone: () => void;
  clearLinkProspectPhone: () => void;
  clearLinkWorkforcePhone: () => void;
  clearLinkApplicantPhone: () => void;
  clearLinkVendorPhone: () => void;
  clearLinkVendorProspectPhone: () => void;
  openContactDetails: (
    phoneNumber: string,
    contact?: IContact | null,
    mode?:
      | "create"
      | "link"
      | "both"
      | "link-client"
      | "link-workforce"
      | "link-prospect"
      | "link-applicant"
      | "link-vendor"
      | "link-vendor-prospect",
  ) => void;
  closeContactDetails: () => void;
  openAddContactMenu: (phoneNumber: string, anchorEl: HTMLElement) => void;
}

const VirtualAssistantContext = createContext<VrContextProps | undefined>(undefined);

export const useVirtualAssistant = () => {
  const ctx = useContext(VirtualAssistantContext);
  if (!ctx) throw new Error("useTwilio must be used within TwilioProvider");
  return ctx;
};

/** Safe outside VirtualAssistantProvider (e.g. Contacts dashboard). */
export const useOptionalVirtualAssistant = () => useContext(VirtualAssistantContext);

interface Props {
  children: ReactNode;
}

/* ===================== HELPERS ===================== */

const isSameCompanyKey = (key: string, uid?: string, companyId?: string | null) => {
  if (!uid || !companyId) return false;

  const parsed = parseDeviceKey(key);

  return parsed.uid === uid && parsed.companyId === String(companyId);
};

const parseDeviceKey = (key: string) => {
  const firstSeparator = key.indexOf(":");
  const secondSeparator = key.indexOf(":", firstSeparator + 1);

  return {
    uid: key.slice(0, firstSeparator),
    companyId: key.slice(firstSeparator + 1, secondSeparator),
    phoneNumber: key.slice(secondSeparator + 1),
  };
};

const createInitialCompanyState = (key: string, sessionNumber = ""): CompanyRuntimeState => ({
  companyId: parseDeviceKey(key).companyId,
  sessionNumber,
  status: "initializing",
  errorMessage: undefined,
  activeCall: null,
  callDirection: null,
  remotePhone: null,
  isMuted: false,
  isOnHold: false,
  callDuration: 0,
});

const resolveCompanyView = (state?: CompanyRuntimeState | null): CallView => {
  if (!state?.activeCall) return "dialer";
  if (state.callDirection === "INCOMING") return "incoming";
  return "in-call";
};

const buildDeviceKey = (uid: string, companyId: string, phoneNumber: string) => `${uid}:${companyId}:${phoneNumber}`;

/* ===================== PROVIDER ===================== */

export const VirtualAssistantProvider: React.FC<Props> = ({ children }) => {
  const devicesRef = useRef<Map<string, Device>>(new Map());
  const callsRef = useRef<Map<string, any>>(new Map());

  const heartbeatsRef = useRef<Map<string, number>>(new Map());
  const callStartsRef = useRef<Map<string, number>>(new Map());
  const companiesStateRef = useRef<Map<string, CompanyRuntimeState>>(new Map());
  const callTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const connectedCallsRef = useRef<Set<string>>(new Set());
  const initializingDevicesRef = useRef<Set<string>>(new Set());

  const uid = useAppSelector((state) => state.userState.uid);
  const company = useAppSelector((state) => state.userState.company);
  const userCompanies = useAppSelector((state) => state.userState.companies);
  const companies = useMemo(() => userCompanies ?? [], [userCompanies]);
  const { openModal, closeByKey } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const queryClient = useQueryClient();
  const requireMicrophone = useRequireMicrophoneForCall();

  const [selectedNumberUid, setSelectedNumberUid] = useState<string>("");
  const [companiesState, setCompaniesState] = useState<Map<string, CompanyRuntimeState>>(new Map());
  const [selectedContact, setSelectedContact] = useState<SmsContact | null>(null);

  const [contactToEdit, setContactToEdit] = useState<IContact | null>(null);
  const [quickSavePhone, setQuickSavePhone] = useState<string | null>(null);
  const [contactMode, setContactMode] = useState<
    "create" | "link" | "both" | "link-client" | "link-workforce" | "link-prospect" | "link-applicant" | "link-vendor" | "link-vendor-prospect"
  >("both");
  const [linkClientPhone, setLinkClientPhone] = useState<string | null>(null);
  const [linkProspectPhone, setLinkProspectPhone] = useState<string | null>(null);
  const [linkWorkforcePhone, setLinkWorkforcePhone] = useState<string | null>(null);
  const [linkApplicantPhone, setLinkApplicantPhone] = useState<string | null>(null);
  const [linkVendorPhone, setLinkVendorPhone] = useState<string | null>(null);
  const [linkVendorProspectPhone, setLinkVendorProspectPhone] = useState<string | null>(null);
  const [addContactMenu, setAddContactMenu] = useState<{
    phoneNumber: string;
    anchorEl: HTMLElement;
  } | null>(null);
  const [blockConfirm, setBlockConfirm] = useState<{
    phoneNumber: string;
    anchorEl: HTMLElement;
    mode: "block" | "unblock";
    uid?: string;
  } | null>(null);
  const [isBlockingNumber, setIsBlockingNumber] = useState(false);
  const [attachPersonMenuAnchor, setAttachPersonMenuAnchor] = useState<HTMLElement | null>(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<"customer" | "workforce" | "vendor" | null>(null);

  const openAddContactMenu = useCallback((phoneNumber: string, anchorEl: HTMLElement) => {
    setAddContactMenu({ phoneNumber, anchorEl });
  }, []);

  const closeAddContactMenu = useCallback(() => {
    setAddContactMenu(null);
    setAttachPersonMenuAnchor(null);
    setSubMenuAnchorEl(null);
    setActiveSubMenu(null);
  }, []);

  const handleConfirmBlockNumber = useCallback(async () => {
    if (!blockConfirm || isBlockingNumber) {
      return;
    }

    setIsBlockingNumber(true);

    try {
      if (blockConfirm.mode === "unblock") {
        if (!blockConfirm.uid) {
          return;
        }

        await unblockPhoneNumber(blockConfirm.uid);
        showSnackbar("Number unblocked", "success");
      } else {
        await blockPhoneNumber(blockConfirm.phoneNumber);
        showSnackbar("Number blocked", "success");
      }

      await queryClient.invalidateQueries({ queryKey: blockedNumbersQueryKey(company?.id) });
      setBlockConfirm(null);
    } catch (error) {
      const responseMessage = isAxiosError(error) ? error.response?.data?.message : undefined;
      const fallback = blockConfirm.mode === "unblock" ? "Could not unblock this number" : "Could not block this number";
      const message = typeof responseMessage === "string" ? responseMessage : fallback;
      showSnackbar(message, "error");
    } finally {
      setIsBlockingNumber(false);
    }
  }, [blockConfirm, company?.id, isBlockingNumber, queryClient, showSnackbar]);

  const clearLinkClientPhone = useCallback(() => {
    setLinkClientPhone(null);
  }, []);

  const clearLinkProspectPhone = useCallback(() => {
    setLinkProspectPhone(null);
  }, []);

  const clearLinkWorkforcePhone = useCallback(() => {
    setLinkWorkforcePhone(null);
  }, []);

  const clearLinkApplicantPhone = useCallback(() => {
    setLinkApplicantPhone(null);
  }, []);

  const clearLinkVendorPhone = useCallback(() => {
    setLinkVendorPhone(null);
  }, []);

  const clearLinkVendorProspectPhone = useCallback(() => {
    setLinkVendorProspectPhone(null);
  }, []);

  const openContactDetails = useCallback(
    (
      phoneNumber: string,
      contact: IContact | null = null,
      mode:
        | "create"
        | "link"
        | "both"
        | "link-client"
        | "link-workforce"
        | "link-prospect"
        | "link-applicant"
        | "link-vendor"
        | "link-vendor-prospect" = "both",
    ) => {
      if (mode === "link-client") {
        closeByKey("Virtual-Receptionist-Contact-Details-Modal");
        setQuickSavePhone(null);
        setLinkClientPhone(phoneNumber);
        return;
      }

      if (mode === "link-prospect") {
        closeByKey("Virtual-Receptionist-Contact-Details-Modal");
        setQuickSavePhone(null);
        setLinkProspectPhone(phoneNumber);
        return;
      }

      if (mode === "link-workforce") {
        closeByKey("Virtual-Receptionist-Contact-Details-Modal");
        setQuickSavePhone(null);
        setLinkWorkforcePhone(phoneNumber);
        return;
      }

      if (mode === "link-applicant") {
        closeByKey("Virtual-Receptionist-Contact-Details-Modal");
        setQuickSavePhone(null);
        setLinkApplicantPhone(phoneNumber);
        return;
      }

      if (mode === "link-vendor") {
        closeByKey("Virtual-Receptionist-Contact-Details-Modal");
        setQuickSavePhone(null);
        setLinkVendorPhone(phoneNumber);
        return;
      }

      if (mode === "link-vendor-prospect") {
        closeByKey("Virtual-Receptionist-Contact-Details-Modal");
        setQuickSavePhone(null);
        setLinkVendorProspectPhone(phoneNumber);
        return;
      }

      setQuickSavePhone(phoneNumber);
      setContactToEdit(contact);
      setContactMode(mode);

      const rawType = ((contact as any)?.type || (contact as any)?.role || (contact as any)?.contactType || "").toLowerCase();

      const isResolvedClient = contact && (rawType.includes("client") || rawType.includes("prospect") || !!(contact as any).clientUid);

      const isResolvedWorkforce =
        contact &&
        (rawType.includes("workforce") ||
          rawType.includes("employee") ||
          rawType.includes("supervisor") ||
          rawType.includes("contractor") ||
          rawType.includes("applicant") ||
          !!(contact as any).workforceUid);

      const isResolvedVendor = contact && (rawType.includes("vendor") || rawType.includes("supplier") || !!(contact as any).vendorUid);

      let modalKey = "Virtual-Receptionist-Contact-Details-Modal";
      if (isResolvedClient && contact) {
        const clientUid = (contact as any).clientUid || contact.uid;
        modalKey = `update-client-${clientUid}`;
      } else if (isResolvedWorkforce && contact) {
        modalKey = "Workforce-Portfolio-Details-Modal";
      } else if (isResolvedVendor && contact) {
        const vendorUid = (contact as any).vendorUid || contact.uid;
        modalKey = `update-vendor-${vendorUid}`;
      }

      openModal(modalKey);
    },
    [openModal, closeByKey],
  );

  const closeContactDetails = useCallback(() => {
    setContactToEdit(null);
    setQuickSavePhone(null);
    setContactMode("both");
    setLinkClientPhone(null);
    setLinkProspectPhone(null);
    setLinkWorkforcePhone(null);
    setLinkApplicantPhone(null);
  }, []);

  const currentCompanyId = company?.id ? String(company.id) : null;
  const companiesIds = companies.map((item) => String(item.companyId));

  const { data: companyNumbersData = [] } = useCompanyNumbersMultiQuery(companiesIds);
  const companyNumbersDataRef = useRef(companyNumbersData);
  const blockedNumbersQuery = useBlockedNumbersQuery(company?.id);
  const blockedEntryForMenu = findBlockedNumber(blockedNumbersQuery.data ?? [], addContactMenu?.phoneNumber);
  const isUnblocking = blockConfirm?.mode === "unblock";

  useEffect(() => {
    companyNumbersDataRef.current = companyNumbersData;
  }, [companyNumbersData]);

  /* ===================== SELECTED PHONE ===================== */

  useEffect(() => {
    if (!currentCompanyId) {
      setSelectedNumberUid("");
      return;
    }

    if (!companyNumbersData.length) return;

    const currentCompanyNumbers = companyNumbersData.find((item) => String(item.companyId) === String(currentCompanyId));

    const phoneNumbers = (currentCompanyNumbers?.phoneNumbers || []).filter((number) => number.status === CompanyNumberStatus.ACTIVE);

    if (!phoneNumbers.length) {
      setSelectedNumberUid("");
      return;
    }

    const selectedNumberBelongsToCurrentCompany = phoneNumbers.some((number) => number.uid === selectedNumberUid);

    if (!selectedNumberUid || !selectedNumberBelongsToCurrentCompany) {
      setSelectedNumberUid(phoneNumbers[0].uid);
    }
  }, [companyNumbersData, currentCompanyId, selectedNumberUid]);

  const selectedPhoneNumber = useMemo(() => {
    if (!currentCompanyId || !selectedNumberUid) return null;

    const currentCompanyNumbers = companyNumbersData.find((item) => String(item.companyId) === String(currentCompanyId));

    const found = currentCompanyNumbers?.phoneNumbers.find((number) => number.uid === selectedNumberUid);

    return found?.phoneNumber || null;
  }, [companyNumbersData, currentCompanyId, selectedNumberUid]);

  /* ===================== HELPERS ===================== */

  const getCurrentPhoneNumber = useCallback(
    (companyId?: string): string | null => {
      const resolvedCompanyId = companyId || currentCompanyId;

      if (!resolvedCompanyId) {
        return selectedPhoneNumber || null;
      }

      const activeEntry = Array.from(companiesStateRef.current.entries()).find(([key, state]) => {
        const parsed = parseDeviceKey(key);

        return parsed.uid === uid && parsed.companyId === currentCompanyId && state.activeCall;
      });

      if (activeEntry) {
        const [key, state] = activeEntry;

        if (state.sessionNumber) {
          return state.sessionNumber;
        }

        const { phoneNumber } = parseDeviceKey(key);

        return phoneNumber;
      }

      if (String(resolvedCompanyId) === String(currentCompanyId)) {
        return selectedPhoneNumber || null;
      }

      const companyNumbers = companyNumbersDataRef.current.find((item) => String(item.companyId) === String(resolvedCompanyId));

      const firstPhoneNumber = companyNumbers?.phoneNumbers?.[0]?.phoneNumber;

      return firstPhoneNumber || null;
    },
    [currentCompanyId, selectedPhoneNumber],
  );

  const currentPhoneNumber = useMemo(() => {
    return getCurrentPhoneNumber(currentCompanyId || undefined);
  }, [getCurrentPhoneNumber, currentCompanyId]);

  const updateCompanyState = useCallback((key: string, updates: Partial<CompanyRuntimeState>) => {
    setCompaniesState((prev) => {
      const next = new Map(prev);
      const current = next.get(key) || createInitialCompanyState(key);
      next.set(key, { ...current, ...updates });
      return next;
    });
  }, []);

  const getCompanyCallState = useCallback((key: string): CompanyRuntimeState | null => {
    return companiesStateRef.current.get(key) || null;
  }, []);

  const getCompanyView = useCallback((key: string): CallView => {
    const state = companiesStateRef.current.get(key);
    return resolveCompanyView(state);
  }, []);

  const findCallKey = useCallback(
    (predicate: (state: CompanyRuntimeState) => boolean = (state) => Boolean(state.activeCall)) => {
      const entries = Array.from(companiesStateRef.current.entries());

      const inCurrentCompany = entries.find(([key, state]) => isSameCompanyKey(key, uid, currentCompanyId) && predicate(state));
      if (inCurrentCompany) return inCurrentCompany[0];

      return entries.find(([, state]) => predicate(state))?.[0] ?? null;
    },
    [currentCompanyId, uid],
  );

  const currentCompanyCallEntry = useMemo(
    () => Array.from(companiesState.entries()).find(([key, state]) => isSameCompanyKey(key, uid, currentCompanyId) && state.activeCall) ?? null,
    [companiesState, currentCompanyId, uid],
  );

  const activeCallEntry = useMemo(
    () => currentCompanyCallEntry ?? Array.from(companiesState.entries()).find(([, state]) => state.activeCall) ?? null,
    [companiesState, currentCompanyCallEntry],
  );

  const activeCallCompanyId = activeCallEntry ? parseDeviceKey(activeCallEntry[0]).companyId : null;

  const activeCallCompanyName = useMemo(() => {
    if (!activeCallCompanyId || !currentCompanyId) return null;
    if (String(activeCallCompanyId) === String(currentCompanyId)) return null;

    const membership: any = companies.find((item: any) => String(item?.companyId) === String(activeCallCompanyId));
    return membership?.company?.businessName || membership?.company?.name || null;
  }, [activeCallCompanyId, companies, currentCompanyId]);

  const currentCompanyBaseState = useMemo(() => {
    if (!currentCompanyId || !selectedPhoneNumber) return null;
    return companiesState.get(buildDeviceKey(uid!, currentCompanyId, selectedPhoneNumber)) || null;
  }, [companiesState, currentCompanyId, selectedPhoneNumber, uid]);

  const currentCompanyState = activeCallEntry?.[1] ?? currentCompanyBaseState;

  const currentView = useMemo(() => resolveCompanyView(currentCompanyState), [currentCompanyState]);

  const isOnHold = currentCompanyState?.isOnHold ?? false;

  const incomingCalls = useMemo<IncomingCompanyCall[]>(() => {
    return Array.from(companiesState.entries())
      .filter(([_, item]) => item.activeCall && item.callDirection === "INCOMING")
      .map(([key, item]) => ({
        key,
        companyId: item.companyId,
        remotePhone: item.remotePhone,
        activeCall: item.activeCall,
        sessionNumber: item.sessionNumber,
        isInternalIncoming: item.isInternalIncoming,
        internalCallerName: item.internalCallerName,
        internalCallerCompanyName: item.internalCallerCompanyName,
      }));
  }, [companiesState]);

  const sessionNumber = (currentCompanyCallEntry?.[1] ?? currentCompanyBaseState)?.sessionNumber || "";

  /* ===================== BACKEND ===================== */

  type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

  const fetchFromBackend = useCallback(async (endpoint: string, method: HttpMethod = "GET", body?: any, companyId?: string) => {
    const response = await gatewayPhone.request({
      url: endpoint,
      method,
      data: body,
      headers: {
        ...(companyId ? { "x-company-id": String(companyId) } : {}),
      },
    });

    return response.data;
  }, []);

  const invalidateCallHistory = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["call-history"],
      refetchType: "all",
    });
  }, [queryClient]);

  /* ===================== CALL TIMER ===================== */

  const startCallTimer = useCallback(
    (key: string) => {
      callStartsRef.current.set(key, Date.now());

      const existingTimer = callTimersRef.current.get(key);
      if (existingTimer) clearInterval(existingTimer);

      const interval = setInterval(() => {
        const startTime = callStartsRef.current.get(key);
        if (!startTime) return;
        const duration = Math.floor((Date.now() - startTime) / 1000);
        updateCompanyState(key, { callDuration: duration });
      }, 1000);

      callTimersRef.current.set(key, interval);
      return interval;
    },
    [updateCompanyState],
  );

  /* ===================== CALL SID ===================== */

  const getCurrentCallSid = useCallback((key: string): string | null => {
    const currentCall = callsRef.current.get(key);
    if (!currentCall) return null;

    return (
      currentCall.customParameters?.get?.("callSid") ||
      currentCall.customParameters?.get?.("CallSid") ||
      currentCall.parameters?.CallSid ||
      currentCall.parameters?.callSid ||
      null
    );
  }, []);

  const notifyAgentAnswered = useCallback(
    async (key: string, companyId: string) => {
      const callSid = getCurrentCallSid(key);
      if (!callSid || !uid) return;

      const agentIdentity = key;

      await fetchFromBackend(
        "/twilio/webhooks/agent-answered",
        "POST",
        {
          callSid,
          agentIdentity, //"uid:companyId:phoneNumber"
        },
        companyId,
      );
    },
    [fetchFromBackend, getCurrentCallSid, uid],
  );

  /* ===================== HOLD ===================== */

  const toggleHoldByCompany = useCallback(
    (key: string) => {
      const call = callsRef.current.get(key);
      if (!call) return;

      const state = companiesStateRef.current.get(key);
      const next = !state?.isOnHold;
      call.mute(next);
      call
        .getRemoteStream?.()
        ?.getAudioTracks?.()
        .forEach((track: MediaStreamTrack) => {
          track.enabled = !next;
        });
      updateCompanyState(key, { isOnHold: next });
    },
    [updateCompanyState],
  );

  const toggleHold = useCallback(() => {
    const activeKey = findCallKey();
    if (activeKey) {
      toggleHoldByCompany(activeKey);
      return;
    }

    if (!currentCompanyId || !selectedPhoneNumber) return;
    toggleHoldByCompany(buildDeviceKey(uid!, currentCompanyId, selectedPhoneNumber));
  }, [currentCompanyId, findCallKey, selectedPhoneNumber, uid, toggleHoldByCompany]);

  /* ===================== TRANSFER ===================== */

  const handleTransfer = useCallback(
    async (key: string, targetAgentUid: string) => {
      const callSid = getCurrentCallSid(key);

      if (!callSid) {
        throw new Error("There is no active call to transfer.");
      }

      const { companyId } = parseDeviceKey(key);

      await fetchFromBackend("/twilio/transfer", "POST", { callSid, targetAgentUid }, companyId);
    },
    [getCurrentCallSid, fetchFromBackend],
  );

  /* ===================== HEARTBEAT ===================== */

  const stopHeartbeat = useCallback((key: string) => {
    const heartbeat = heartbeatsRef.current.get(key);
    if (heartbeat) clearInterval(heartbeat);
    heartbeatsRef.current.delete(key);
  }, []);

  const startHeartbeat = useCallback(
    (key: string, companyId: string, phoneNumber: string) => {
      stopHeartbeat(key);

      const identity = `${uid}:${companyId}:${phoneNumber}`;

      fetchFromBackend("/twilio/heartbeat", "POST", { identity, phoneNumber }, companyId).catch(() => {});

      const heartbeat = window.setInterval(() => {
        fetchFromBackend("/twilio/heartbeat", "POST", { identity, phoneNumber }, companyId).catch(() => {});
      }, 15000);

      heartbeatsRef.current.set(key, heartbeat);
    },
    [fetchFromBackend, stopHeartbeat, uid],
  );

  const sendDisconnect = useCallback(
    async (companyId: string) => {
      try {
        await fetchFromBackend("/twilio/disconnect", "POST", {}, companyId);
      } catch {}
    },
    [fetchFromBackend],
  );

  /* ===================== DEVICE TEARDOWN ===================== */

  const teardownDevice = useCallback(
    (key: string) => {
      stopHeartbeat(key);

      const timer = callTimersRef.current.get(key);
      if (timer) clearInterval(timer);
      callTimersRef.current.delete(key);

      const device = devicesRef.current.get(key);
      if (device) {
        void sendDisconnect(parseDeviceKey(key).companyId);
        device.destroy();
      }

      devicesRef.current.delete(key);
      initializingDevicesRef.current.delete(key);
      callsRef.current.delete(key);
      callStartsRef.current.delete(key);
      connectedCallsRef.current.delete(key);

      setCompaniesState((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    },
    [sendDisconnect, stopHeartbeat],
  );

  /* ===================== END CALL ===================== */

  const notifyCallReleased = useCallback(
    async (key: string) => {
      const callSid = getCurrentCallSid(key);
      if (!callSid || !uid) return;

      const { companyId } = parseDeviceKey(key);

      try {
        await fetchFromBackend("/twilio/call/released", "POST", { callSid, agentIdentity: key }, companyId);
      } catch {
        // Best-effort fallback; heartbeat reconciliation covers missed webhooks.
      }
    },
    [fetchFromBackend, getCurrentCallSid, uid],
  );

  const endCall = useCallback(
    (key: string, options?: { notifyReleased?: boolean }) => {
      if (options?.notifyReleased && connectedCallsRef.current.has(key)) {
        void notifyCallReleased(key);
      }

      connectedCallsRef.current.delete(key);

      const timer = callTimersRef.current.get(key);
      if (timer) {
        clearInterval(timer);
        callTimersRef.current.delete(key);
      }

      callStartsRef.current.delete(key);
      callsRef.current.delete(key);

      const device = devicesRef.current.get(key);
      if (device) device.audio?.incoming(false);

      updateCompanyState(key, {
        activeCall: null,
        callDirection: null,
        remotePhone: null,
        callDuration: 0,
        isMuted: false,
        isOnHold: false,
        isInternalIncoming: false,
        internalCallerName: null,
        internalCallerCompanyName: null,
      });

      void invalidateCallHistory();
    },
    [updateCompanyState, invalidateCallHistory, notifyCallReleased],
  );

  /* ===================== CALL HANDLERS ===================== */

  const handleIncomingCall = useCallback(
    (key: string, companyId: string, call: any) => {
      console.log("[incoming-call]", {
        key,
        status: call.status?.(),
        from: call.parameters?.From,
        to: call.parameters?.To,
        params: call.parameters,
      });

      callsRef.current.set(key, call);

      const internalIncoming = readInternalIncomingCall(call);
      const raw = internalIncoming.isInternal ? null : call.customParameters?.get?.("callerNumber") || call.parameters?.From || null;

      updateCompanyState(key, {
        activeCall: call,
        callDirection: "INCOMING",
        isMuted: false,
        remotePhone: splitPhoneFromE164(raw),
        isInternalIncoming: internalIncoming.isInternal,
        internalCallerName: internalIncoming.callerDisplayName,
        internalCallerCompanyName: internalIncoming.callerCompanyName,
      });

      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(internalIncoming.isInternal ? "Incoming internal call" : "Incoming call", {
          body: internalIncoming.callerDisplayName || raw || "Unknown number",
          tag: `incoming-call-${companyId}-${raw || internalIncoming.callerDisplayName || "unknown"}`,
        });
      }

      let ended = false;

      const onEnd = (reason: string) => (e?: any) => {
        if (ended) return;
        ended = true;

        console.warn("[Twilio Incoming End]", {
          key,
          reason,
          callStatus: call.status?.(),
          direction: call.direction,
          error: e,
        });

        endCall(key);
      };

      call.on("cancel", onEnd("cancel"));
      call.on("disconnect", onEnd("disconnect"));
      call.on("reject", onEnd("reject"));
      call.on("error", onEnd("error"));

      call.on("accept", () => {
        console.log("[incoming-call] accepted");

        updateCompanyState(key, {
          callDirection: null,
          isMuted: false,
        });

        startCallTimer(key);
      });

      call.on("warning", (warning: any) => {
        console.warn("[Twilio Warning]", warning);
      });

      call.on("warning-cleared", (warning: any) => {
        console.warn("[Twilio Warning Cleared]", warning);
      });
    },
    [endCall, startCallTimer, updateCompanyState],
  );

  /* ===================== DEVICE INIT ===================== */

  const initDevice = useCallback(
    async (key: string, phoneNumber: string, companyId: string) => {
      if (initializingDevicesRef.current.has(key)) {
        return;
      }

      initializingDevicesRef.current.add(key);

      const existingDevice = devicesRef.current.get(key);
      if (existingDevice) {
        initializingDevicesRef.current.delete(key);
        return;
      }

      try {
        if (!sessionStorage.getItem("token")) {
          throw new Error("No active session found.");
        }

        const { token } = await fetchFromBackend(
          `/twilio/token?phoneNumber=${encodeURIComponent(phoneNumber)}&companyId=${encodeURIComponent(companyId)}`,
          "GET",
          undefined,
          companyId,
        );

        updateCompanyState(key, {
          companyId,
          status: "initializing",
          errorMessage: undefined,
          sessionNumber: phoneNumber,
        });

        const device = new Device(token, {
          logLevel: "error",
          allowIncomingWhileBusy: true,
        });

        device.on("registered", () => {
          console.log("[device] registered");
          updateCompanyState(key, { status: "ready" });
          startHeartbeat(key, companyId, phoneNumber);
        });

        device.on("unregistered", () => {
          console.log("[device] unregistered");
          updateCompanyState(key, { status: "offline" });
          stopHeartbeat(key);
        });

        device.on("tokenWillExpire", async () => {
          try {
            console.log("[device] token will expire", {
              key,
              companyId,
              phoneNumber,
            });

            const { token: newToken } = await fetchFromBackend(
              `/twilio/token?phoneNumber=${encodeURIComponent(phoneNumber)}&companyId=${encodeURIComponent(companyId)}`,
              "GET",
              undefined,
              companyId,
            );

            device.updateToken(newToken);

            console.log("[device] token updated", {
              key,
              companyId,
              phoneNumber,
            });
          } catch (error: any) {
            console.error("[device-token-refresh-error]", {
              key,
              companyId,
              phoneNumber,
              status: error?.response?.status,
              data: error?.response?.data,
              message: error?.message,
            });

            devicesRef.current.delete(key);
            initializingDevicesRef.current.delete(key);

            void initDevice(key, phoneNumber, companyId);
          }
        });

        device.on("error", (err: any) => {
          console.error("[device-error]", err);

          if (err?.code === 31002) return;

          if (err?.code === 31205) {
            devicesRef.current.delete(key);
            initializingDevicesRef.current.delete(key);
            initDevice(key, phoneNumber, companyId);
            return;
          }

          updateCompanyState(key, {
            status: "error",
            errorMessage: err?.message,
          });
        });

        device.on("incoming", (call: any) => {
          console.log("[incoming-call]", call.parameters);
          handleIncomingCall(key, companyId, call);
        });

        await device.register();

        device.audio?.incoming(false);
        devicesRef.current.set(key, device);
      } catch (err: any) {
        updateCompanyState(key, {
          status: "error",
          errorMessage: err?.message,
        });
      } finally {
        initializingDevicesRef.current.delete(key);
      }
    },
    [fetchFromBackend, handleIncomingCall, startHeartbeat, stopHeartbeat, updateCompanyState],
  );

  /* ===================== PUBLIC ACTIONS - BY KEY ===================== */

  const acceptCallByCompany = useCallback(
    async (key: string) => {
      const call = callsRef.current.get(key);
      if (!call) return;

      const { companyId } = parseDeviceKey(key);

      const canUseMicrophone = await requireMicrophone();

      if (!canUseMicrophone) {
        try {
          if (call.direction === "INCOMING" && call.status?.() === "pending") {
            call.reject();
          } else {
            call.disconnect();
          }
        } catch (error) {
          console.error("[call-cleanup-error]", error);
        }

        endCall(key);
        return;
      }

      try {
        call.accept();

        void invalidateCallHistory();

        void notifyAgentAnswered(key, companyId).catch((err) => {
          console.error("Error notifying answered agent:", err);
        });

        startCallTimer(key);

        updateCompanyState(key, {
          isMuted: false,
          callDirection: null,
        });
      } catch (err) {
        console.error("Error accepting call:", err);

        try {
          if (call.direction === "INCOMING" && call.status?.() === "pending") {
            call.reject();
          } else {
            call.disconnect();
          }
        } catch (cleanupError) {
          console.error("[call-cleanup-after-accept-error]", cleanupError);
        }

        endCall(key);

        showSnackbar(getCallStartMediaErrorMessage(err), "error", 6000, "bottom-center");

        endCall(key);
      }
    },
    [endCall, invalidateCallHistory, notifyAgentAnswered, requireMicrophone, showSnackbar, startCallTimer, updateCompanyState],
  );

  const hangupCallByCompany = useCallback(
    (key: string) => {
      const call = callsRef.current.get(key);

      if (!call) {
        endCall(key);
        return;
      }

      try {
        const callStatus = call.status?.();
        const direction = call.direction;

        if (direction === "INCOMING" && callStatus === "pending") {
          call.reject();
          endCall(key);
          return;
        }

        call.disconnect();
      } catch (err) {
        console.error("Error hanging up:", err);
        endCall(key, {
          notifyReleased: connectedCallsRef.current.has(key),
        });
      }
    },
    [endCall],
  );

  const toggleMuteByCompany = useCallback(
    (key: string) => {
      const call = callsRef.current.get(key);
      if (!call) return;

      const muted = !call.isMuted();
      call.mute(muted);
      updateCompanyState(key, { isMuted: muted });
    },
    [updateCompanyState],
  );

  const sendDigitByCompany = useCallback((key: string, digit: string) => {
    const call = callsRef.current.get(key);
    call?.sendDigits(digit);
  }, []);

  /* ===================== PUBLIC ACTIONS - CURRENT COMPANY ONLY ===================== */

  const makeCall = useCallback(
    async (to: string) => {
      if (!to) return;

      if (!currentCompanyId || !selectedPhoneNumber) {
        console.error("No active company or no number selected");
        return;
      }

      const hasActiveVoipCall = Array.from(companiesStateRef.current.values()).some(
        (state) => state.callDirection === "OUTGOING" || (state.activeCall && state.callDirection !== "INCOMING"),
      );

      if (hasActiveVoipCall) {
        showSnackbar("You can only have one active VoIP call at a time.", "warning");
        return;
      }

      const canUseMicrophone = await requireMicrophone();
      if (!canUseMicrophone) return;

      const key = buildDeviceKey(uid!, currentCompanyId, selectedPhoneNumber);
      const device = devicesRef.current.get(key);

      if (!device) {
        console.error("Device not initialized for:", key);
        showSnackbar("Voice is not ready yet. Try again in a moment.", "warning");
        return;
      }

      try {
        const call = await device.connect({
          params: {
            To: to,
            companyId: String(currentCompanyId),
            fromNumber: selectedPhoneNumber,
          },
        });

        updateCompanyState(key, {
          callDirection: "OUTGOING",
          remotePhone: splitPhoneFromE164(to),
          isMuted: false,
        });

        void invalidateCallHistory();
        callsRef.current.set(key, call);
        updateCompanyState(key, { activeCall: call });

        call.on("accept", () => {
          connectedCallsRef.current.add(key);
          startCallTimer(key);
        });

        let ended = false;
        const onEnd = () => {
          if (ended) return;
          ended = true;
          endCall(key);
        };

        call.on("disconnect", () => endCall(key, { notifyReleased: true }));
        call.on("reject", onEnd);
        call.on("error", onEnd);
      } catch (error) {
        console.error("[voip-makeCall] failed", error);
        showSnackbar(getCallStartMediaErrorMessage(error), "error", 6000, "bottom-center");
        endCall(key);
      }
    },
    [currentCompanyId, selectedPhoneNumber, endCall, invalidateCallHistory, requireMicrophone, startCallTimer, updateCompanyState, showSnackbar],
  );

  const acceptCall = useCallback(() => {
    const incomingKey = findCallKey((state) => Boolean(state.activeCall) && state.callDirection === "INCOMING");

    if (incomingKey) {
      void acceptCallByCompany(incomingKey);
      return;
    }
    if (!selectedPhoneNumber || !currentCompanyId) return;
    void acceptCallByCompany(buildDeviceKey(uid!, currentCompanyId, selectedPhoneNumber));
  }, [currentCompanyId, findCallKey, selectedPhoneNumber, uid, acceptCallByCompany]);

  const hangupCall = useCallback(() => {
    const activeKey = findCallKey();

    if (activeKey) {
      hangupCallByCompany(activeKey);
      return;
    }

    if (!selectedPhoneNumber || !currentCompanyId) return;
    hangupCallByCompany(buildDeviceKey(uid!, currentCompanyId, selectedPhoneNumber));
  }, [currentCompanyId, findCallKey, selectedPhoneNumber, uid, hangupCallByCompany]);

  const toggleMute = useCallback(() => {
    const activeKey = findCallKey();
    if (!activeKey) return;
    toggleMuteByCompany(activeKey);
  }, [findCallKey, toggleMuteByCompany]);

  const sendDigit = useCallback(
    (digit: string) => {
      const activeKey = findCallKey();
      if (!activeKey) return;
      sendDigitByCompany(activeKey, digit);
    },
    [findCallKey, sendDigitByCompany],
  );

  /* ===================== SMS ===================== */

  const handleSelectContact = useCallback((contact: SmsContact | null) => {
    setSelectedContact(contact);
  }, []);

  /* ===================== MOCK ===================== */

  const mockIncomingCall = useCallback(
    (companyIdArg?: string) => {
      const companyId = companyIdArg || currentCompanyId;
      if (!companyId) return;

      const mockPhone = "+15551234567";
      const key = buildDeviceKey(uid!, companyId, mockPhone);

      const fakeCall = {
        parameters: { From: mockPhone },
        status: () => "pending",
        direction: "INCOMING",
        on: () => {},
        reject: () => endCall(key),
        accept: () => {},
      };

      callsRef.current.set(key, fakeCall);

      updateCompanyState(key, {
        companyId,
        activeCall: fakeCall,
        remotePhone: splitPhoneFromE164(mockPhone),
        callDirection: "INCOMING",
        isMuted: false,
      });
    },
    [currentCompanyId, endCall, updateCompanyState],
  );

  /* ===================== MULTI DEVICE REGISTRATION ===================== */

  useEffect(() => {
    if (!companyNumbersData?.length) return;

    const validKeys = new Set<string>();

    companyNumbersData.forEach(({ companyId, phoneNumbers }) => {
      phoneNumbers.forEach(({ phoneNumber }) => {
        validKeys.add(buildDeviceKey(uid!, companyId, phoneNumber));
      });
    });

    Array.from(devicesRef.current.keys()).forEach((key) => {
      if (validKeys.has(key)) return;
      if (callsRef.current.has(key)) return;

      teardownDevice(key);
    });

    companyNumbersData.forEach(({ companyId, phoneNumbers }) => {
      if (!companyId || !phoneNumbers.length) return;

      phoneNumbers.forEach(({ phoneNumber }) => {
        const key = buildDeviceKey(uid!, companyId, phoneNumber);

        const current = companiesStateRef.current.get(key);

        if (current?.sessionNumber !== phoneNumber) {
          updateCompanyState(key, {
            companyId,
            sessionNumber: phoneNumber,
          });
        }

        if (!devicesRef.current.has(key) && !initializingDevicesRef.current.has(key)) {
          void initDevice(key, phoneNumber, companyId);
        }
      });
    });
  }, [companyNumbersData, initDevice, teardownDevice, updateCompanyState, uid]);

  /* ===================== CLEANUP ON UNMOUNT ===================== */

  useEffect(() => {
    registerVoiceSessionCleanup(async () => {
      heartbeatsRef.current.forEach((_, key) => stopHeartbeat(key));
    });

    return () => {
      registerVoiceSessionCleanup(null);
    };
  }, [stopHeartbeat]);

  useEffect(() => {
    return () => {
      Array.from(devicesRef.current.keys()).forEach((key) => teardownDevice(key));

      heartbeatsRef.current.forEach((_, key) => stopHeartbeat(key));
      callTimersRef.current.forEach((timer) => clearInterval(timer));
      callTimersRef.current.clear();
      callsRef.current.clear();
      callStartsRef.current.clear();
    };
  }, [stopHeartbeat, teardownDevice]);

  /* ===================== SYNC REFS ===================== */

  useEffect(() => {
    companiesStateRef.current = companiesState;
  }, [companiesState]);

  /* ===================== SMS SOCKET ===================== */

  useSmsSocket(sessionNumber, companyNumbersData);

  /* ===================== CONTEXT VALUE ===================== */

  return (
    <VirtualAssistantContext.Provider
      value={{
        companyNumbersData,

        sessionNumber,
        status: currentCompanyState?.status || "initializing",
        errorMessage: currentCompanyState?.errorMessage,

        activeCall: currentCompanyState?.activeCall || null,
        callDirection: currentCompanyState?.callDirection || null,
        remotePhone: currentCompanyState?.remotePhone || null,
        view: currentView,
        isMuted: currentCompanyState?.isMuted || false,
        callDuration: currentCompanyState?.callDuration || 0,

        getCurrentPhoneNumber,
        currentPhoneNumber,

        currentCompanyState,
        activeCallCompanyName,
        companiesState,
        incomingCalls,
        getCompanyCallState,
        getCompanyView,

        makeCall,

        acceptCall,
        hangupCall,
        toggleMute,
        sendDigit,

        acceptCallByCompany,
        hangupCallByCompany,
        toggleMuteByCompany,
        sendDigitByCompany,

        handleSelectContact,
        selectedContact,
        mockIncomingCall,

        sendDisconnect,
        handleTransfer,

        selectedNumberUid,
        setSelectedNumberUid,

        isOnHold,
        toggleHold,
        toggleHoldByCompany,
        contactToEdit,
        quickSavePhone,
        contactMode,
        linkClientPhone,
        linkProspectPhone,
        linkWorkforcePhone,
        linkApplicantPhone,
        linkVendorPhone,
        linkVendorProspectPhone,
        clearLinkClientPhone,
        clearLinkProspectPhone,
        clearLinkWorkforcePhone,
        clearLinkApplicantPhone,
        clearLinkVendorPhone,
        clearLinkVendorProspectPhone,
        openContactDetails,
        closeContactDetails,
        openAddContactMenu,
      }}
    >
      {children}
      <Menu
        anchorEl={addContactMenu?.anchorEl}
        open={Boolean(addContactMenu)}
        onClose={() => {
          if (!attachPersonMenuAnchor) closeAddContactMenu();
        }}
        container={menuPropsModal.container}
        PaperProps={{
          style: {
            ...menuPropsModal.PaperProps.style,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            minWidth: 240,
            padding: "4px",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (addContactMenu) {
              openContactDetails(addContactMenu.phoneNumber, null, "create");
            }
            closeAddContactMenu();
          }}
          sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
        >
          <ListItemIcon>
            <PersonAddIcon fontSize="small" sx={{ color: "#fc9700" }} />
          </ListItemIcon>
          <ListItemText primary="Quick Contact 2" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            setSubMenuAnchorEl(e.currentTarget);
            setActiveSubMenu("customer");
          }}
          selected={activeSubMenu === "customer"}
          sx={{ py: 1.2, px: 2, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <ListItemIcon>
              <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
            </ListItemIcon>
            <ListItemText primary="Customer" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
          </div>
          <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary", ml: 1 }} />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            setSubMenuAnchorEl(e.currentTarget);
            setActiveSubMenu("workforce");
          }}
          selected={activeSubMenu === "workforce"}
          sx={{ py: 1.2, px: 2, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <ListItemIcon>
              <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
            </ListItemIcon>
            <ListItemText primary="Workforce" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
          </div>
          <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary", ml: 1 }} />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            setSubMenuAnchorEl(e.currentTarget);
            setActiveSubMenu("vendor");
          }}
          selected={activeSubMenu === "vendor"}
          sx={{ py: 1.2, px: 2, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <ListItemIcon>
              <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
            </ListItemIcon>
            <ListItemText primary="Vendor" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
          </div>
          <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary", ml: 1 }} />
        </MenuItem>

        {blockedNumbersQuery.isSuccess && (
          <>
            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={() => {
                if (!addContactMenu) {
                  return;
                }

                setBlockConfirm({
                  phoneNumber: addContactMenu.phoneNumber,
                  anchorEl: addContactMenu.anchorEl,
                  mode: blockedEntryForMenu ? "unblock" : "block",
                  uid: blockedEntryForMenu?.uid,
                });
                closeAddContactMenu();
              }}
              sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
            >
              <ListItemIcon>
                {blockedEntryForMenu ? (
                  <LockOpenIcon fontSize="small" sx={{ color: "#fc9700" }} />
                ) : (
                  <BlockIcon fontSize="small" sx={{ color: "error.main" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={blockedEntryForMenu ? "Unblock number" : "Block number"}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: 600,
                  color: blockedEntryForMenu ? "text.primary" : "error.main",
                }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      <Menu
        anchorEl={subMenuAnchorEl}
        open={Boolean(subMenuAnchorEl) && Boolean(activeSubMenu)}
        onClose={() => {
          setSubMenuAnchorEl(null);
          setActiveSubMenu(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        container={menuPropsModal.container}
        PaperProps={{
          style: {
            ...menuPropsModal.PaperProps.style,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            minWidth: 200,
            padding: "4px",
          },
        }}
      >
        {activeSubMenu === "customer" && (
          <>
            <MenuItem
              onClick={() => {
                if (addContactMenu) {
                  openContactDetails(addContactMenu.phoneNumber, null, "link-prospect");
                }
                closeAddContactMenu();
              }}
              sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
            >
              <ListItemIcon>
                <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
              </ListItemIcon>
              <ListItemText primary="Prospect" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (addContactMenu) {
                  openContactDetails(addContactMenu.phoneNumber, null, "link-client");
                }
                closeAddContactMenu();
              }}
              sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
            >
              <ListItemIcon>
                <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
              </ListItemIcon>
              <ListItemText primary="Client" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
            </MenuItem>
          </>
        )}

        {activeSubMenu === "workforce" && (
          <>
            <MenuItem
              onClick={() => {
                if (addContactMenu) {
                  openContactDetails(addContactMenu.phoneNumber, null, "link-applicant");
                }
                closeAddContactMenu();
              }}
              sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
            >
              <ListItemIcon>
                <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
              </ListItemIcon>
              <ListItemText primary="Applicant" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (addContactMenu) {
                  openContactDetails(addContactMenu.phoneNumber, null, "link-workforce");
                }
                closeAddContactMenu();
              }}
              sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
            >
              <ListItemIcon>
                <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
              </ListItemIcon>
              <ListItemText primary="Workforce" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
            </MenuItem>
          </>
        )}

        {activeSubMenu === "vendor" && (
          <>
            <MenuItem
              onClick={() => {
                if (addContactMenu) {
                  openContactDetails(addContactMenu.phoneNumber, null, "link-vendor-prospect");
                }
                closeAddContactMenu();
              }}
              sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
            >
              <ListItemIcon>
                <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
              </ListItemIcon>
              <ListItemText primary="Prospect" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (addContactMenu) {
                  openContactDetails(addContactMenu.phoneNumber, null, "link-vendor");
                }
                closeAddContactMenu();
              }}
              sx={{ py: 1.2, px: 2, borderRadius: "8px" }}
            >
              <ListItemIcon>
                <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
              </ListItemIcon>
              <ListItemText primary="Vendor" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
            </MenuItem>
          </>
        )}
      </Menu>

      <Popover
        open={Boolean(blockConfirm)}
        anchorEl={blockConfirm?.anchorEl}
        onClose={() => {
          if (!isBlockingNumber) {
            setBlockConfirm(null);
          }
        }}
        container={menuPropsModal.container}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 250,
            borderRadius: 3,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            zIndex: 99999999,
          },
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 0.5 }}>
          {isUnblocking ? "Unblock this number?" : "Block this number?"}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "var(--fx-surface-textsecondary, #64748B)", mb: 2 }}>
          Are you sure you want to {isUnblocking ? "unblock" : "block"}{" "}
          <Box component="span" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>
            {formatUSPhone(blockConfirm?.phoneNumber) || blockConfirm?.phoneNumber}
          </Box>
          ? {isUnblocking ? "Future calls from it will be allowed again." : "Future calls from it will be rejected."}
        </Typography>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            size="small"
            disabled={isBlockingNumber}
            onClick={() => setBlockConfirm(null)}
            sx={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "none",
              color: "var(--fx-surface-textsecondary, #64748B)",
              borderRadius: 1.5,
              py: 0.5,
              px: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={isUnblocking ? "primary" : "error"}
            disabled={isBlockingNumber}
            onClick={() => {
              void handleConfirmBlockNumber();
            }}
            sx={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "none",
              borderRadius: 1.5,
              boxShadow: "none",
              py: 0.5,
              px: 1.5,
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {isUnblocking ? "Unblock" : "Block"}
          </Button>
        </Box>
      </Popover>
    </VirtualAssistantContext.Provider>
  );
};
